import type { NoteWorkflowsDocument, WorkflowNodeSelection } from "./noteWorkflowsTypes";

/** Stored on workflows created before per-box viewing/signing permissions. */
export interface LegacyBoxTaskSettings {
    permissionVersion?: never;
    responsibleMemberIds: string[];
    signerMemberIds: string[];
    notificationRecipients: { memberId: string; email: string }[];
}

export interface BoxPermissionSettings {
    permissionVersion: 2;
    signerMemberIds: string[];
    viewerMemberIds: string[];
}

export type BoxTaskSettings = LegacyBoxTaskSettings | BoxPermissionSettings;
export type BoxPermissionMemberKey = "signerMemberIds" | "viewerMemberIds";

export const emptyTaskSettings = (): BoxPermissionSettings => ({
    permissionVersion: 2,
    signerMemberIds: [],
    viewerMemberIds: []
});

/** Convert only the editing draft; legacy responsibilities never grant view access. */
export function editableBoxPermissions(settings?: BoxTaskSettings): BoxPermissionSettings {
    return {
        permissionVersion: 2,
        signerMemberIds: [...(settings?.signerMemberIds ?? [])],
        viewerMemberIds: settings?.permissionVersion === 2 ? [...settings.viewerMemberIds] : []
    };
}

/** Preserve one empty selection row while editing; never copy a choice into the other permission. */
export function updatePermissionMembers(settings: BoxPermissionSettings, key: BoxPermissionMemberKey, memberIds: string[]): BoxPermissionSettings {
    return { ...settings, [key]: [...new Set(memberIds)] };
}

export function serializeBoxPermissions(settings: BoxPermissionSettings): BoxPermissionSettings {
    return {
        permissionVersion: 2,
        signerMemberIds: [...new Set(settings.signerMemberIds.filter(Boolean))],
        viewerMemberIds: [...new Set(settings.viewerMemberIds.filter(Boolean))]
    };
}

export function selectedTaskSettings(document: NoteWorkflowsDocument, selection: WorkflowNodeSelection | null): BoxTaskSettings | undefined {
    if (!selection || selection.nodeKind === "trigger") return undefined;
    const workflow = document.workflows.find(item => item.id === selection.workflowId);
    if (!workflow) return undefined;
    const nodes = [...workflow.steps, ...workflow.outcomes, ...(workflow.subflows ?? []), ...(workflow.subflows ?? []).flatMap(flow => flow.steps)];
    return nodes.find(node => node.id === selection.nodeId)?.task_settings;
}
